// src/sim/catch/free-all.ts — P3 liberator-touches-plinth free-all (GAME-06, D-14).
//
// Invariants:
//   - Pure: returns { updates, events }; never mutates inputs (Pitfall 1).
//   - Emits AT MOST ONE `hider-released` event per tick (D-14 — not one per
//     freed hider).
//   - freedIds contains every caught-at-base hider freed at this tick, sorted
//     by id for deterministic replay.
//   - Every freed hider gets freedImmunityUntilTick = currentTick +
//     P3_FREED_IMMUNITY_TICKS (re-catch guard, Pitfall 9).
//   - liberatorId chosen as smallest id (sort-asc); no RNG — D-14 doesn't
//     mandate stochastic selection.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-14.

import type { Entity, WorldState } from '../../shared/types/world';
import type { MapData } from '../../shared/types/map';
import type { GameEvent } from '../../shared/types/events';
import { getPlinthRing } from '../movement';
import { P1_CHARACTER_RADIUS } from '../movement';
import {
  P3_FREED_IMMUNITY_TICKS,
  P3_PLINTH_RING_CONTACT_SLACK_PX,
  P3_FLEE_FROM_BASE_MAX_TICKS,
  P3_CATCH_FREE_COOLDOWN_TICKS,
} from './constants';

export interface FreeAllResult {
  updates: Record<string, Entity>;
  events: GameEvent[];
}

export function applyFreeAll(
  world: WorldState,
  mapData: MapData | null | undefined,
  currentTick: number,
): FreeAllResult {
  const updates: Record<string, Entity> = {};
  const events: GameEvent[] = [];
  if (!mapData) return { updates, events };
  const ring = getPlinthRing(mapData);
  if (ring === null) return { updates, events };

  // Debug session cascade-prevention (2026-04-28): liberator + caughtAtBase
  // membership now keys off CONTACT WITH THE PLINTH RING, not "inside the
  // plinth polygon". The ring is a small (radius P3_PLINTH_RING_RADIUS_PX)
  // solid obstacle at the polygon centroid. A character is "in contact"
  // when its circle is at or below (ringRadius + characterRadius) from the
  // ring center, plus a small slack equal to half the player diameter
  // (P3_PLINTH_RING_CONTACT_SLACK_PX). Touching = qualifying. Replacing
  // the polygon-zone check stops the cascade where a hider could walk
  // anywhere inside the (large) polygon and re-trigger free-all.
  const contactThreshold = ring.radius + P1_CHARACTER_RADIUS + P3_PLINTH_RING_CONTACT_SLACK_PX;
  const contactThresholdSq = contactThreshold * contactThreshold;
  const inRingContact = (pos: { x: number; y: number }): boolean => {
    const dx = pos.x - ring.center.x;
    const dy = pos.y - ring.center.y;
    return dx * dx + dy * dy <= contactThresholdSq;
  };

  // Pass 0 — liberate RE-ARM (debug session free-all-misfire-frozen-hider,
  // BUG#3b 2026-06-11).
  //
  // Background: BUG#3 (the earlier over-correction) gated the liberator on
  // `freedImmunityUntilTick === null`. Because that field is never cleared
  // during a round (only reset at team-switch), it permanently barred ANY
  // hider that was EVER freed from triggering free-all again — so a
  // previously-freed player who ran back to the base with captives present
  // got nothing. That breaks the core loop, since hiders are caught and
  // freed repeatedly over a round.
  //
  // Correct model: re-arm on RING EXIT. A hider is "armed" to liberate by
  // default (liberateArmed undefined/true). The moment it is freed (whether
  // as the liberator or as a freed captive) it is DISARMED at the ring
  // (liberateArmed = false) — see the free-loop below. It re-arms only once
  // it is a FREE hider that has actually LEFT ring contact. This is
  // timing-independent: a just-freed hider lingering at the ring stays
  // disarmed no matter how long it lingers (so it cannot re-trigger), while
  // a hider that disperses and genuinely returns is re-armed and CAN trigger
  // a fresh free-all. Catch immunity (catch.ts) and AI liberate-suppression
  // (ai-controller.ts) still key off freedImmunityUntilTick and are
  // unaffected.
  for (const e of Object.values(world.entities)) {
    if (e.role !== 'hider') continue;
    if (e.liberateArmed !== false) continue; // already armed (true/undefined)
    if (e.status !== 'free') continue; // only free hiders re-arm
    if (inRingContact(e.position)) continue; // must have left the ring
    updates[e.id] = { ...e, liberateArmed: true };
  }

  // Pass 1: identify liberators — free, non-hidden hiders TOUCHING the
  // plinth ring AND currently ARMED (have not been freed-at-ring without
  // since dispersing). liberateArmed !== false admits never-freed hiders
  // (undefined) and re-armed returnees (true) alike; it excludes a hider
  // freed at the ring this round that has not yet left (false).
  const liberators = Object.values(world.entities)
    .filter(
      (e) =>
        e.role === 'hider' &&
        e.status === 'free' &&
        e.hidingSpotId === null &&
        e.floorSpot === null &&
        e.liberateArmed !== false &&
        inRingContact(e.position),
    )
    .sort((a, b) => a.id.localeCompare(b.id));
  if (liberators.length === 0) return { updates, events };

  // Pass 2: identify caughtAtBase — caught hiders parked AT the ring
  // (escortedBy null AND in ring contact). Mid-escort captives are
  // excluded.
  const caughtAtBase = Object.values(world.entities)
    .filter(
      (e) =>
        e.role === 'hider' &&
        e.status === 'caught' &&
        e.escortedBy === null &&
        inRingContact(e.position) &&
        // MECHANIC 2 (2026-06-12): catch->free cooldown. A freshly-deposited
        // captive (caughtAtTick stamped by applyDropOff) is NOT freeable until
        // P3_CATCH_FREE_COOLDOWN_TICKS have elapsed, breaking the catch/free
        // thrash loop. caughtAtTick undefined (e.g. hand-staged fixtures) means
        // no cooldown applies. After the window the captive frees normally.
        !(
          e.caughtAtTick != null &&
          currentTick - e.caughtAtTick < P3_CATCH_FREE_COOLDOWN_TICKS
        ),
    )
    .sort((a, b) => a.id.localeCompare(b.id));
  if (caughtAtBase.length === 0) return { updates, events };

  const liberator = liberators[0]!;
  const freedIds: string[] = [];

  for (const hider of caughtAtBase) {
    freedIds.push(hider.id);
    updates[hider.id] = {
      ...hider,
      status: 'free',
      escortedBy: null,
      freedImmunityUntilTick: currentTick + P3_FREED_IMMUNITY_TICKS,
      // BUG#3b: disarm the freed captive at the ring — it must leave ring
      // contact (Pass 0 re-arm) before it can itself trigger a free-all.
      liberateArmed: false,
      // MECHANIC 1 (2026-06-12): true flee-from-base. The AIController overrides
      // the hider BT with a full-speed radial-away-from-base move until the hider
      // clears the ring or this deadline elapses, so freed captives actually run
      // clear instead of twitching / random-walking at the plinth.
      fleeFromBaseUntilTick: currentTick + P3_FLEE_FROM_BASE_MAX_TICKS,
    };
  }
  // Debug session freed-hiders-dont-disperse follow-up (2026-04-28): the
  // LIBERATOR also needs liberate-suppression so they walk away from the
  // plinth post-liberation. Setting freedImmunityUntilTick on the liberator
  // hooks them into the scorer suppression so they pick deep/move instead
  // of liberate on subsequent decide ticks.
  updates[liberator.id] = {
    ...liberator,
    freedImmunityUntilTick: currentTick + P3_FREED_IMMUNITY_TICKS,
    // BUG#3b: disarm the liberator at the ring too — it must disperse (leave
    // ring contact) before it can re-trigger. This is what keeps a lingering
    // liberator from re-firing free-all on the very next tick, while still
    // allowing it to liberate again after a genuine round-trip.
    liberateArmed: false,
    // MECHANIC 1 (2026-06-12): the liberator flees too. It triggered the free-all
    // at the ring; without this it twitched in place. The radial-away override
    // runs it clear of the base before normal hider AI resumes.
    fleeFromBaseUntilTick: currentTick + P3_FLEE_FROM_BASE_MAX_TICKS,
  };
  events.push({
    type: 'hider-released',
    liberatorId: liberator.id,
    freedIds,
    tick: currentTick,
  });
  return { updates, events };
}
